'use client';

import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import MapLocation from '@alltrails/amplitude/enums/MapLocation';
import useConditionPlayback from '@alltrails/conditions/hooks/useConditionPlayback';
import useLogMapOverlayTimeDrawerActionTapped from '@alltrails/conditions/hooks/useLogMapOverlayTimeDrawerActionTapped';
import { useGetTrailWeatherOverviewQuery } from '@alltrails/conditions/redux/conditionsApi';
import { getLocalizedDateStart, getUTCFromLocalized } from '@alltrails/conditions/utils/timeHelpers';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import { exploreConditionsIntervalMin } from '@/hooks/useExploreConditions';
import useExploreTrailSurfaceTypes from '@/hooks/useExploreTrailSurfaceTypes';
import useIsConditionsSidebarMounted from '@/hooks/useIsConditionsSidebarMounted';
import { useAppSelector } from '@/utils/redux';
import styles from './ExploreMap.module.scss';

const FallFoliageLegend = dynamic(() => import('@alltrails/advanced-map-layers/components/FallFoliageLegend/FallFoliageLegend'), { ssr: false });
const PhotoHeatmapLegend = dynamic(() => import('@alltrails/advanced-map-layers/components/PhotoHeatmapLegend/PhotoHeatmapLegend'), { ssr: false });
const PublicLandsLegend = dynamic(() => import('@alltrails/advanced-map-layers/components/PublicLandsLegend/PublicLandsLegend'), { ssr: false });
const RecordingEndLegend = dynamic(() => import('@alltrails/advanced-map-layers/components/RecordingEndLegend/RecordingEndLegend'), { ssr: false });
const RecordingStartLegend = dynamic(() => import('@alltrails/advanced-map-layers/components/RecordingStartLegend/RecordingStartLegend'), {
  ssr: false
});
const SlopeAngleLegend = dynamic(() => import('@alltrails/advanced-map-layers/components/SlopeAngleLegend/SlopeAngleLegend'), { ssr: false });
const SurfaceTypeLegend = dynamic(() => import('@alltrails/maps/components/SurfaceTypeLegend/SurfaceTypeLegend'), { ssr: false });
const ConditionLegend = dynamic(() => import('@alltrails/conditions/components/ConditionLegend'), { ssr: false });
const HeatmapLegend = dynamic(() => import('@alltrails/heatmaps/components/HeatmapLegend'), { ssr: false });

type MapLegendsProps = {
  mapLocation: MapLocation;
};

const MapLegends = ({ mapLocation }: MapLegendsProps) => {
  const { activeCondition, selectedUTC, displayMetric, dynamicStyleIds, customRouteId, trailId } = useAppSelector(state => ({
    activeCondition: state.conditions.activeCondition,
    selectedUTC: state.conditions.selectedUTC,
    displayMetric: state.context.displayMetric,
    dynamicStyleIds: state.map.dynamicStyleIds,
    customRouteId: state.customRoute.route.id,
    trailId: state.explore.currentTrail?.id
  }));
  const isMobile = useIsMobileSizedScreen();
  const isConditionsSidebarMounted = useIsConditionsSidebarMounted();
  const surfaceTypes = useExploreTrailSurfaceTypes();

  const experiments = useExperiments();
  const isPhotoHeatmapEnabled = experiments?.['web-tmt-rm-photos-overlay']?.value === 'on';
  const isRecordingOverlaysEnabled = experiments?.['web-tmt-rm-recording-overlays']?.value === 'on';
  const heatmapIsApplied = useMemo(() => dynamicStyleIds.includes('newHeatmap'), [dynamicStyleIds]);
  const publicLandsIsApplied = useMemo(() => dynamicStyleIds.includes('publicLands'), [dynamicStyleIds]);
  const slopeAngleIsApplied = useMemo(() => dynamicStyleIds.includes('slopeAngle'), [dynamicStyleIds]);
  const photoHeatmapIsApplied = useMemo(
    () => isPhotoHeatmapEnabled && dynamicStyleIds.includes('photoHeatmap'),
    [isPhotoHeatmapEnabled, dynamicStyleIds]
  );
  const recordingStartIsApplied = useMemo(
    () => isRecordingOverlaysEnabled && dynamicStyleIds.includes('recordingStart'),
    [isRecordingOverlaysEnabled, dynamicStyleIds]
  );
  const recordingEndIsApplied = useMemo(
    () => isRecordingOverlaysEnabled && dynamicStyleIds.includes('recordingEnd'),
    [isRecordingOverlaysEnabled, dynamicStyleIds]
  );
  const fallFoliageIsApplied = useMemo(() => dynamicStyleIds.includes('fallFoliage'), [dynamicStyleIds]);
  // On a trail page `customRoute.route.id` holds the trail's at-map id, not a custom route, so only report it where
  // the page really is a custom route.
  const reportedCustomRouteId = mapLocation === 'custom route' ? customRouteId : undefined;
  const logDateChangedEvent = useLogMapOverlayTimeDrawerActionTapped('date changed', mapLocation, trailId, reportedCustomRouteId);
  const logTimeScrubbedEvent = useLogMapOverlayTimeDrawerActionTapped('time scrubbed', mapLocation, trailId, reportedCustomRouteId);
  const logTimePlayedEvent = useLogMapOverlayTimeDrawerActionTapped('time played', mapLocation, trailId, reportedCustomRouteId);
  const logTimePausedEvent = useLogMapOverlayTimeDrawerActionTapped('time paused', mapLocation, trailId, reportedCustomRouteId);

  // These args must stay identical to what the conditions sidebar passes to `source.useOverview`, so the two
  // share one RTK Query cache entry instead of each issuing a request.
  const { data: overviewData } = useGetTrailWeatherOverviewQuery(
    { sentenceUnits: displayMetric ? 'm' : 'i', trailId: trailId ?? -1 },
    { skip: !activeCondition || trailId === undefined }
  );

  // The return of getTimezoneOffset is in minutes and positive values represent times behind UTC,
  // but we want seconds and negative values for behind UTC
  const browserUTCOffsetSeconds = useMemo(() => new Date().getTimezoneOffset() * -60, []);
  // Falls back to the browser's timezone on pages with no trail to localize to.
  const utcOffsetSeconds = overviewData?.secondsFromUtc ?? browserUTCOffsetSeconds;

  // Falls back to seven bare days so the picker still offers a week when the overview has no summaries to show.
  const days = useMemo(() => {
    const dailySummaries = overviewData?.dailySummaries;
    if (!dailySummaries?.length) {
      return Array(7).fill({});
    }
    return dailySummaries.map(({ highTemperature, iconCode, lowTemperature }) => ({
      tempHigh: highTemperature,
      tempLow: lowTemperature,
      weatherIconCode: iconCode
    }));
  }, [overviewData?.dailySummaries]);

  const sidebarOwnsDaySelect = isConditionsSidebarMounted && activeCondition === 'snowDepth';
  const showDayAndTimeControls = !sidebarOwnsDaySelect;
  const dayStartUTC = selectedUTC
    ? // Convert the selected utc -> local, get the local day start, convert the local day start -> utc day start
      getUTCFromLocalized(getLocalizedDateStart(selectedUTC, utcOffsetSeconds), utcOffsetSeconds)
    : undefined;
  useConditionPlayback(exploreConditionsIntervalMin, dayStartUTC);

  return (
    <div className={styles.mapLegendContainer}>
      {/* The conditions overlays and heatmap overlays are mutually exclusive, so we only show one at a time to avoid jarring transitions */}
      {activeCondition && activeCondition !== 'groundConditions' ? (
        <ConditionLegend
          activeCondition={activeCondition}
          dayAndTimeControls={
            showDayAndTimeControls
              ? {
                  days,
                  intervalMinutes: exploreConditionsIntervalMin,
                  onDayChange: logDateChangedEvent,
                  onPause: logTimePausedEvent,
                  onPlay: logTimePlayedEvent,
                  onTimeChange: logTimeScrubbedEvent,
                  utcOffsetSeconds
                }
              : undefined
          }
        />
      ) : (
        <HeatmapLegend isOpen={heatmapIsApplied} />
      )}
      {isMobile && <PublicLandsLegend isOpen={publicLandsIsApplied} />}
      <SurfaceTypeLegend isOpen={surfaceTypes.isApplied} surfaceTypeAggregation={surfaceTypes.aggregation} useMetric={displayMetric} />
      <SlopeAngleLegend isOpen={slopeAngleIsApplied} />
      <PhotoHeatmapLegend isOpen={photoHeatmapIsApplied} />
      <FallFoliageLegend isOpen={fallFoliageIsApplied} mapLocation={mapLocation} trailId={trailId} customRouteId={reportedCustomRouteId} />
      <RecordingStartLegend isOpen={recordingStartIsApplied} />
      <RecordingEndLegend isOpen={recordingEndIsApplied} />
    </div>
  );
};

export default MapLegends;
